import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty, message } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";

import { FaBuilding, FaRegClock } from "react-icons/fa";
import { FaLocationDot, FaIndustry } from "react-icons/fa6";
import { HiBriefcase } from "react-icons/hi2";
import { MdHomeWork } from "react-icons/md";

import {
  Buttons,
  Spacer,
  popover,
  boxContainer,
  inputFields,
  checkbox,
  GetProPopup,
  GuestUserPopup,
  Loader,
} from "../../components";

import { UtilityArrays, Images } from "../../config";

import { InternshipsAction } from "../../store/actions";

import "./styles.css";

const API_KEY = process.env.REACT_APP_CHATGPT_API_KEY;

const Internships = () => {
  const dispatch = useDispatch();

  const { internships, isLoading, jobCompanies, jobTypes } = useSelector(
    (state) => state.internships
  );
  const { countries } = useSelector((state) => state.others);
  const profileData = useSelector((state) => state.profile?.user);

  const [loadingResume, setLoadingResume] = useState(false);
  const [activeInternship, setActiveInternship] = useState(null);
  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    search: "",
    location: [],
    company: "",
    date: "",
    jobType: "",
  });

  useEffect(() => {
    handleSearch();
  }, [filters]);

  useEffect(() => {
    dispatch(InternshipsAction.getJobCompanies());
    dispatch(InternshipsAction.getJobTypes());
  }, []);
  useEffect(() => {
    if (internships?.length > 0) {
      setActiveInternship(internships[0]);
    }
  }, [internships]);

  const getBase64Image = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching profile image:", error);
      return null;
    }
  };

  const generateResume = async () => {
    if (!profileData) {
      message.error("Profile data not found. Please log in.");
      return;
    }

    setLoadingResume(true);

    const prompt = `Create a structured resume in bullet points:
    
    Full Name: ${profileData.firstName} ${profileData.lastName}
    Email: ${profileData.email}
    Phone: +${profileData.phoneNumber}  if available
    Age: ${profileData.student?.age}  if available
    Address: ${profileData.city}  if available,${profileData.country}
    Institute: ${
      profileData.schoolName || profileData.individualStudent?.schoolName
    }  if available
    Grade: ${profileData.student?.grade} if available
    
    - Write a short professional summary based on ${
      profileData.about
    } if available
    - List top skills (5-7)
    - Provide structured job experience with roles & achievements
    - Education history (degrees, years)
    - Certifications or additional qualifications
    `;

    try {
      const apiRequestBody = {
        model: "gpt-4",
        messages: [{ role: "user", content: prompt }],
      };
      const response = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiRequestBody),
        }
      );
      const resData = await response.json();

      const resumeText = resData?.choices[0]?.message?.content;

      if (profileData.profileImage) {
        const profileImageBase64 = await getBase64Image(
          profileData.profileImage
        );
        createResumePDF(resumeText, profileImageBase64);
      } else {
        createResumePDF(resumeText, null);
      }
    } catch (error) {
      console.error("Error generating resume:", error);
      message.error("Failed to generate resume. Please try again.");
    } finally {
      setLoadingResume(false);
    }
  };

  const createResumePDF = (text, profileImageBase64) => {
    const doc = new jsPDF();
    doc.setFont("times", "normal");
    let yPos = 20;

    if (profileImageBase64) {
      doc.addImage(profileImageBase64, "JPEG", 80, yPos, 50, 50);
      yPos += 60;
    }

    doc.setFontSize(18);
    doc.setFont("times", "bold");
    doc.text(`${profileData.firstName} ${profileData.lastName}`, 20, yPos);
    doc.setFontSize(12);
    doc.setFont("times", "normal");
    doc.text(`Email: ${profileData.email}`, 20, (yPos += 8));
    doc.text(
      `Phone: +${profileData.phoneNumber || "Not Provided"}`,
      20,
      (yPos += 8)
    );
    doc.text(
      `Age: ${profileData?.student?.age || "Not Provided"}`,
      20,
      (yPos += 8)
    );
    doc.text(
      `Institute: ${
        profileData.schoolName || profileData.individualStudent?.schoolName
      }`,
      20,
      (yPos += 8)
    );

    // Divider Line
    doc.setLineWidth(0.5);
    doc.line(20, (yPos += 10), 190, yPos);

    // Parse AI-generated resume into sections
    const sections = text.split("\n\n");
    doc.setFontSize(14);

    sections.forEach((section) => {
      if (section.trim() !== "") {
        // Add section title
        doc.setFont("times", "bold");
        doc.text(section.split("\n")[0], 20, (yPos += 10));

        // Add section content
        doc.setFont("times", "normal");
        section
          .split("\n")
          .slice(1)
          .forEach((line) => {
            doc.text(`• ${line}`, 25, (yPos += 7));
          });

        // Add spacing between sections
        yPos += 5;

        // Page Break if necessary
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
      }
    });

    // Save the generated resume
    doc.save("resume.pdf");
    message.success("Resume downloaded successfully!");
  };

  const handleSearch = () => {
    const payload = {
      ...filters,
      location: JSON.stringify(filters.location),
    };
    setActiveInternship(null);
    if (!profileData?.freeUser) {
      dispatch(InternshipsAction.getInternships(payload));
    }
  };

  const countrylist = countries?.map((country) => ({
    label: country?.name,
    value: country?.id,
  }));

  const dateValues = [
    { title: "Posted this week", value: "Posted this week" },
    { title: "Posted last week", value: "Posted last week" },
    { title: "Posted this month", value: "Posted this month" },
    { title: "Posted in last 6 months", value: "Posted in last 6 months" },
    { title: "Posted this year", value: "Posted this year" },
  ];

  const filterButtons = [
    {
      title: "Date Posted",
      items: (
        <div className="filter-dropdown">
          {dateValues?.map((item, index) => (
            <div
              key={index}
              onClick={() => setFilters({ ...filters, date: item.value })}
              className={`${
                filters.date === item.value
                  ? "active-filter-value"
                  : "filter-value"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Company",
      items: (
        <div className="filter-dropdown max-h-[20em] overflow-auto">
          {jobCompanies?.map((item, index) => (
            <div
              key={index}
              onClick={() => setFilters({ ...filters, company: item.id })}
              className={`${
                filters.company === item.id
                  ? "active-filter-value"
                  : "filter-value"
              }`}
            >
              {item.companyName}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Job Type",
      items: (
        <div className="filter-dropdown">
          {jobTypes?.map((item, index) => (
            <div
              key={index}
              onClick={() => setFilters({ ...filters, jobType: item.id })}
              className={`${
                filters.jobType === item.id
                  ? "active-filter-value"
                  : "filter-value"
              }`}
            >
              {item.title}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Location",
      placement: "bottomLeft",
      items: (
        <div className="w-[400px]">
          <inputFields.SelectDropdownMultiple
            items={countrylist}
            onChange={(value) => setFilters({ ...filters, location: value })}
            value={filters.location}
            allowClear={true}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={`text-black relative`}>
      {profileData?.freeUser && (
        <div className="absolute z-10 left-[35%] top-20">
          <GetProPopup />
        </div>
      )}
      {profileData?.email === "univisionstudent@yopmail.com" && (
        <div className="absolute z-10 left-[35%] top-20">
          <GuestUserPopup />
        </div>
      )}
      <div
        className={`${
          (profileData?.freeUser ||
            profileData?.email === "univisionstudent@yopmail.com") &&
          "blur pointer-events-none"
        }`}
      >
        <div className="text-[1.125em] font-semibold">
          Search For Internships Opportunities
        </div>

        <Spacer height={20} />
        <div className="flex justify-center gap-2 h-[3em]">
          <inputFields.DefaultSearchBox
            onChange={(e) => {
              setFilters({
                ...filters,
                search: e.target.value,
              });
            }}
            disabled={profileData?.freeUser}
          />

          {/* <div className="w-[7.5em]">
            <Buttons.DefaultButton
              size="medium"
              width="full"
              style={{ height: "3em" }}
              onClick={() => generateResume()}
              disabled={profileData?.freeUser}
            >
              Create resume
            </Buttons.DefaultButton>
          </div> */}
        </div>
        <Spacer height={20} />
        <div className="flex gap-2">
          {filterButtons?.map((item, index) => (
            <popover.WhitePopover
              content={item.items}
              placement={item?.placement ?? "bottom"}
              disabled={profileData?.freeUser}
            >
              <Buttons.DefaultButton size="small">
                {item?.title}
              </Buttons.DefaultButton>
            </popover.WhitePopover>
          ))}
        </div>
        <Spacer height={20} />
        <boxContainer.LiteBoxContainer addstyle="p-0">
          {isLoading ? (
            <Loader.Circular />
          ) : (
            <>
              {internships?.length === 0 ? (
                <div className="p-6">
                  <Empty />
                </div>
              ) : (
                <div className="flex">
                  <div className="flex flex-col w-[20%] border-r border-r-gray-300 max-h-[calc(100vh-21em)] overflow-auto">
                    {internships?.map((item, index) => (
                      <div
                        key={index}
                        className={`flex flex-col justify-center gap-2 py-5 pl-[0.9375em] pr-3 cursor-pointer border-b border-b-gray-300 flex-shrink-0 h-[11em] ${
                          index === 0 && "rounded-tl-3xl"
                        } ${
                          activeInternship?.id === item?.id && "bg-[#f6fffc]"
                        }`}
                        onClick={() => setActiveInternship(item)}
                      >
                        <div className="font-semibold flex gap-2">
                          <div className="w-[5%] mt-1">
                            <HiBriefcase fontSize={"1.125em"} />
                          </div>
                          <div className="line-clamp-2 text-ellipsis">
                            {item.title}
                          </div>
                        </div>
                        <div className="font-semibold flex gap-2">
                          <div className="w-[5%] mt-1">
                            <MdHomeWork fontSize={"1.125em"} />
                          </div>
                          <div className="line-clamp-2 text-ellipsis">
                            {item?.company?.companyName}
                          </div>
                        </div>
                        <div className="font-medium flex gap-2">
                          <div className="w-[5%] mt-1">
                            <FaLocationDot fontSize={"1.125em"} />
                          </div>
                          <div>
                            {item.location
                              ? `${item.location}, ${item.country?.name}`
                              : `${item.country?.name}`}
                          </div>
                        </div>
                        <div className="font-medium opacity-70 flex gap-2">
                          <div className="w-[5%] mt-1">
                            <FaRegClock fontSize={"1.125em"} />
                          </div>
                          <div>
                            {moment(item.createdAt).format("DD MMM YYYY")}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-col w-[80%] max-h-[calc(100vh-21em)] overflow-auto">
                    {!activeInternship ? (
                      <div className="flex items-center justify-center text-[1.5em] text-main font-bold h-full">
                        Please select an internship to view details!
                      </div>
                    ) : (
                      <div>
                        <div className="px-6 py-2 border-b border-b-gray-300 flex flex-col justify-between h-[11em]">
                          <div>
                            <div className="text-[1.375em] font-semibold">
                              {activeInternship.title}
                            </div>
                            <Spacer height={10} />
                            <div className="text-[1em] font-semibold flex items-center gap-2">
                              <MdHomeWork fontSize={"1.125em"} />
                              {activeInternship?.company?.companyName}
                            </div>
                            <Spacer height={10} />
                            <div className="font-medium flex gap-2 items-center">
                              <FaLocationDot />
                              {activeInternship?.location
                                ? `${activeInternship?.location}, ${activeInternship?.country?.name}`
                                : `${activeInternship?.country?.name}`}
                            </div>
                          </div>
                          <Spacer height={15} />
                          <div className="flex justify-between items-center">
                            <div className="flex gap-4">
                              {activeInternship?.salary && (
                                <div className="flex gap-2 justify-center items-center px-2 bg-gray-200 rounded-lg font-medium">
                                  <img
                                    src={Images.InternshipsSalary}
                                    className="h-[1em]"
                                  />
                                  {activeInternship?.salary}
                                </div>
                              )}
                              {activeInternship?.employmentType && (
                                <div className="flex gap-2 justify-center items-center px-2 py-1 bg-gray-200 rounded-lg font-medium">
                                  <img
                                    src={Images.InternshipsType}
                                    className="h-[1em]"
                                  />
                                  <span
                                    className="max-w-[15.625em] line-clamp-1 overflow-hidden"
                                    title={
                                      activeInternship?.employmentType?.title
                                    }
                                  >
                                    {activeInternship?.employmentType?.title}
                                  </span>
                                </div>
                              )}
                              {activeInternship?.tags?.length > 0 &&
                                activeInternship?.tags?.map((item, index) => {
                                  console.log(item);
                                  return (
                                    <div className="flex gap-2 justify-center items-center px-2 py-1 bg-gray-200 rounded-lg font-medium">
                                      <FaIndustry fontSize={"1em"} />
                                      <span
                                        className="max-w-[15.625em] line-clamp-1 overflow-hidden"
                                        title={item}
                                      >
                                        {item}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                            <div>
                              <Link
                                to={activeInternship?.jobLink}
                                target="_blank"
                              >
                                <Buttons.DefaultButton size="medium">
                                  Apply
                                </Buttons.DefaultButton>
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="text-[1.25em] font-bold">
                            Job Description
                          </div>
                          <Spacer height={15} />
                          {activeInternship.description
                            .split("\n")
                            .map((line, index) => (
                              <React.Fragment key={index}>
                                {line}
                                <br />
                              </React.Fragment>
                            ))}
                          {/* <div
                            dangerouslySetInnerHTML={{
                              __html: activeInternship.description,
                            }}
                            className="internships"
                          /> */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </boxContainer.LiteBoxContainer>
      </div>
    </div>
  );
};

export default Internships;
